import {
  AddOutlined,
  ArchiveOutlined,
  ArrowBackOutlined,
  ContentCopyOutlined,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  DownloadOutlined,
  HistoryOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  OpenInNewOutlined,
  PublishOutlined,
  SaveOutlined,
  TuneOutlined,
  UploadFileOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  archiveDocumentTemplate,
  createDocumentRule,
  deleteDocumentRule,
  createDocumentTemplate,
  deleteDocumentTemplate,
  DocumentTemplate,
  type DocumentOutputFormat,
  DocumentDictionaryField,
  duplicateDocumentTemplate,
  duplicateDocumentTemplateTranslation,
  exportDocumentTemplates,
  importDocumentTemplates,
  loadDocumentTemplates,
  loadDocumentDictionary,
  loadDocumentAutomationJobs,
  loadDocumentAnchors,
  loadDocumentRules,
  publishDocumentTemplate,
  updateDocumentRule,
  updateDocumentTemplate,
  type DocumentTemplateTransferFile
} from "./api";
import { localDesignerUrl } from "./documentGeneration";

const documentTypes = ["RECEIPT_REPORT", "PUTAWAY_LIST", "QUALITY_INSPECTION_REPORT",
  "REPACKAGING_ORDER", "PICKING_LIST", "PACKING_LIST", "DELIVERY_NOTE",
  "CUSTOMS_CLEARANCE_REPORT", "HANDLING_UNIT_LABEL", "OTHER"] as const;

const systemRuleKeys: Record<string, string> = {
  "Bon de réception automatique": "automaticReceipt", "Automatic receipt report": "automaticReceipt",
  "Document automatique": "automaticDocument", "Automatic document": "automaticDocument",
  "Étiquette HU automatique": "huLabel", "Automatic HU label": "huLabel",
  "Liste de mise en stock automatique": "putawayList", "Automatic put-away list": "putawayList",
  "Fiche qualité automatique": "qualityReport", "Automatic quality report": "qualityReport",
  "Ordre de reconditionnement automatique": "repackagingOrder", "Automatic repackaging order": "repackagingOrder",
  "Liste de préparation automatique": "pickingList", "Automatic picking list": "pickingList",
  "Packing list automatique": "packingList", "Automatic packing list": "packingList",
  "Bon de livraison automatique": "deliveryNote", "Automatic delivery note": "deliveryNote",
  "Fiche de dédouanement automatique": "customsReport", "Automatic customs clearance report": "customsReport",
  "Mission de mise en stock automatique": "putawayMission", "Automatic put-away mission": "putawayMission",
  "Rapport d’inventaire automatique": "inventoryReport", "Automatic inventory report": "inventoryReport"
};

function statusColor(status: DocumentTemplate["status"]): "warning" | "success" | "default" {
  return status === "DRAFT" ? "warning" : status === "PUBLISHED" ? "success" : "default";
}

export function DocumentDesignerPage({
  onBack, canManage
}: { onBack: () => void; canManage: boolean }) {
  const { t, i18n } = useTranslation();
  const interfaceLanguage = i18n.resolvedLanguage?.startsWith("en") ? "en" : "fr";
  const locale = interfaceLanguage === "en" ? "en-US" : "fr-FR";
  const url = localDesignerUrl();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const responses = useRef(new Map<string, (template: Record<string, unknown>) => void>());
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ["document-templates"], queryFn: loadDocumentTemplates
  });
  const [selected, setSelected] = useState<DocumentTemplate | null>(null);
  const [designerReady, setDesignerReady] = useState(false);
  const [name, setName] = useState(() => t("documents.defaults.template"));
  const [documentType, setDocumentType] = useState("RECEIPT_REPORT");
  const [outputFormat, setOutputFormat] = useState<DocumentOutputFormat>("PDF");
  const [zplDpi, setZplDpi] = useState<203 | 300 | 600>(203);
  const [zplWidth, setZplWidth] = useState(100);
  const [zplHeight, setZplHeight] = useState(150);
  const [language, setLanguage] = useState(interfaceLanguage);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(true);
  const [catalogLanguage, setCatalogLanguage] = useState<"fr" | "en" | "all">(interfaceLanguage);
  const [translationDialogOpen, setTranslationDialogOpen] = useState(false);
  const [translationLanguage, setTranslationLanguage] = useState<"fr" | "en">(
    interfaceLanguage === "en" ? "fr" : "en"
  );
  const [automationOpen, setAutomationOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const automationJobs = useQuery({
    queryKey: ["document-automation-jobs"],
    queryFn: loadDocumentAutomationJobs,
    enabled: historyOpen,
    refetchInterval: historyOpen ? 5_000 : false
  });
  const anchors = useQuery({
    queryKey: ["document-anchors"], queryFn: loadDocumentAnchors, enabled: automationOpen
  });
  const rules = useQuery({
    queryKey: ["document-rules"], queryFn: loadDocumentRules, enabled: automationOpen
  });
  const [ruleName, setRuleName] = useState(() => t("documents.defaults.rule"));
  const [ruleAnchor, setRuleAnchor] = useState("INBOUND.RECEIPT.COMPLETED");
  const [ruleTemplate, setRuleTemplate] = useState("");
  const [ruleAction, setRuleAction] = useState<"GENERATE" | "GENERATE_AND_PRINT">("GENERATE");
  const [ruleCopies, setRuleCopies] = useState(1);
  const [rulePrinter, setRulePrinter] = useState("");
  const [ruleBusy, setRuleBusy] = useState(false);
  const translationKey = (value: string) => value.replaceAll(".", "_");
  const dictionaryContextLabel = (context: string) => t(
    `documents.dictionary.contexts.${translationKey(context)}`,
    { defaultValue: context }
  );
  const dictionaryFieldLabel = (field: DocumentDictionaryField) => t(
    `documents.dictionary.fields.${translationKey(field.key)}`,
    { defaultValue: field.label }
  );
  const anchorLabel = (code: string, fallback: string) => t(
    `documents.anchors.${translationKey(code)}`,
    { defaultValue: fallback }
  );
  const ruleNameLabel = (name: string) => systemRuleKeys[name]
    ? t(`documents.automation.systemRules.${systemRuleKeys[name]}`)
    : name;
  const templateFamilies = Array.from(
    (templates.data ?? []).reduce((families, template) => {
      const variants = families.get(template.familyId) ?? [];
      variants.push(template);
      families.set(template.familyId, variants);
      return families;
    }, new Map<string, DocumentTemplate[]>()).values()
  );
  const filteredTemplates = templateFamilies.flatMap((variants) => {
    const preferredLanguage = catalogLanguage === "all" ? interfaceLanguage : catalogLanguage;
    const preferred = variants.find((variant) => variant.language === preferredLanguage);
    // A catalogue language is a preference, not a reason to hide a whole
    // template family. Some operational documents are intentionally supplied
    // in a single language (for example the Boeing purchase requisition in
    // English). Keep such families editable and show the available variant.
    return [preferred ?? variants[0]];
  });

  useEffect(() => {
    setCatalogLanguage(interfaceLanguage);
  }, [interfaceLanguage]);

  const openTranslationDialog = () => {
    if (!selected) return;
    const target = selected.language === "fr" ? "en" : "fr";
    setTranslationLanguage(target);
    setTranslationDialogOpen(true);
  };

  const duplicateTranslation = async () => {
    if (!selected) return;
    await act(
      () => duplicateDocumentTemplateTranslation(selected.id, { language: translationLanguage }),
      t("documents.messages.translationCreated", {
        language: t(`documents.languages.${translationLanguage}`)
      })
    );
    setCatalogLanguage(translationLanguage);
    setTranslationDialogOpen(false);
  };

  const refreshRules = async () => {
    await queryClient.invalidateQueries({ queryKey: ["document-rules"] });
  };

  const createRule = async () => {
    setRuleBusy(true); setError(null);
    try {
      await createDocumentRule({
        name: ruleName, anchorCode: ruleAnchor, templateId: ruleTemplate,
        action: ruleAction, copies: ruleCopies,
        printerCode: ruleAction === "GENERATE_AND_PRINT" ? rulePrinter : null,
        priority: 100, active: true, version: 0
      });
      await refreshRules();
      setMessage(t("documents.messages.ruleCreated"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.errors.create"));
    } finally { setRuleBusy(false); }
  };

  const toggleRule = async (rule: NonNullable<typeof rules.data>[number]) => {
    setRuleBusy(true); setError(null);
    try {
      await updateDocumentRule(rule.id, {
        name: rule.name, anchorCode: rule.anchorCode, templateId: rule.templateId,
        action: rule.action, copies: rule.copies, printerCode: rule.printerCode,
        priority: rule.priority, active: !rule.active, version: rule.version
      });
      await refreshRules();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.errors.update"));
    } finally { setRuleBusy(false); }
  };

  const removeRule = async (rule: NonNullable<typeof rules.data>[number]) => {
    if (!window.confirm(t("documents.automation.deleteConfirm", {
      name: ruleNameLabel(rule.name)
    }))) return;
    setRuleBusy(true); setError(null);
    try {
      await deleteDocumentRule(rule.id, rule.version);
      await refreshRules();
      setMessage(t("documents.messages.ruleDeleted"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.errors.delete"));
    } finally { setRuleBusy(false); }
  };
  const dataContext = selected?.dataContext
    ?? (documentType === "RECEIPT_REPORT" ? "INBOUND_ASN"
      : documentType === "CUSTOMS_CLEARANCE_REPORT" ? "OPERATIONAL_EVENT" : null);
  const dictionaryVersion = selected?.dictionaryVersion
    ?? (["INBOUND_ASN", "OPERATIONAL_EVENT"].includes(dataContext ?? "") ? 1 : null);
  const dictionary = useQuery({
    queryKey: ["document-dictionary", dataContext, dictionaryVersion],
    queryFn: () => loadDocumentDictionary(dataContext!, dictionaryVersion!),
    enabled: Boolean(dataContext && dictionaryVersion)
  });

  const send = useCallback((payload: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage({
      source: "obviowms", ...payload
    }, new URL(url).origin);
  }, [url]);

  const loadInDesigner = useCallback((template: DocumentTemplate | null) => {
    if (!designerReady) return;
    if (template) send({
      type: "load-template", template: template.template,
      outputFormat: template.outputFormat
    });
    else send({
      type: "new-template", outputFormat, dpi: zplDpi,
      width: zplWidth, height: zplHeight
    });
  }, [designerReady, outputFormat, send, zplDpi, zplHeight, zplWidth]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow
          || event.origin !== new URL(url).origin
          || event.data?.source !== "obviowms-pdfme") return;
      if (event.data.type === "designer-ready") setDesignerReady(true);
      if (event.data.type === "template-response" && event.data.requestId) {
        const resolve = responses.current.get(event.data.requestId);
        if (resolve) {
          responses.current.delete(event.data.requestId);
          resolve(event.data.template as Record<string, unknown>);
        }
      }
      if (event.data.type === "bind-result") {
        if (event.data.success) {
          setMessage(event.data.message ?? t("documents.messages.fieldBound"));
          setError(null);
        } else {
          setError(event.data.message ?? t("documents.errors.bind"));
        }
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [t, url]);

  useEffect(() => loadInDesigner(selected), [designerReady, selected, loadInDesigner]);

  const requestTemplate = () => new Promise<Record<string, unknown>>((resolve, reject) => {
    const requestId = crypto.randomUUID();
    const timeout = window.setTimeout(() => {
      responses.current.delete(requestId);
      reject(new Error(t("documents.designer.noResponse")));
    }, 5_000);
    responses.current.set(requestId, (template) => {
      window.clearTimeout(timeout);
      resolve(template);
    });
    send({ type: "request-template", requestId });
  });

  const selectTemplate = (template: DocumentTemplate) => {
    setSelected(template);
    setName(template.name);
    setDocumentType(template.documentType);
    setOutputFormat(template.outputFormat);
    const dpi = Number(template.outputConfiguration.dpi);
    if (dpi === 203 || dpi === 300 || dpi === 600) setZplDpi(dpi);
    const basePdf = template.template.basePdf as { width?: unknown; height?: unknown } | undefined;
    const width = Number(basePdf?.width);
    const height = Number(basePdf?.height);
    if (width > 0) setZplWidth(width);
    if (height > 0) setZplHeight(height);
    setLanguage(template.language);
    setMessage(null);
    setError(null);
  };

  const selectLanguage = (nextLanguage: string) => {
    if (!selected) {
      setLanguage(nextLanguage);
      return;
    }
    const variant = (templates.data ?? []).find(
      (candidate) => candidate.familyId === selected.familyId
        && candidate.language === nextLanguage
    );
    if (variant) selectTemplate(variant);
  };

  const newTemplate = () => {
    setSelected(null);
    setName(t("documents.defaults.template"));
    setDocumentType("RECEIPT_REPORT");
    setOutputFormat("PDF");
    setZplDpi(203);
    setZplWidth(100);
    setZplHeight(150);
    setLanguage(interfaceLanguage);
    setMessage(null);
    setError(null);
    loadInDesigner(null);
  };

  const bindField = (field: DocumentDictionaryField) => {
    setMessage(null); setError(null);
    send({ type: "bind-field", fieldKey: field.key, fieldType: field.type });
  };

  const refresh = async (next?: DocumentTemplate) => {
    await queryClient.invalidateQueries({ queryKey: ["document-templates"] });
    if (next) selectTemplate(next);
  };

  const save = async () => {
    if (!canManage || name.trim().length < 3) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      const template = await requestTemplate();
      const next = selected
        ? await updateDocumentTemplate(selected.id, {
          name, documentType, language, outputFormat,
          outputConfiguration: outputFormat === "ZPL" ? { dpi: zplDpi } : {},
          template, version: selected.version
        })
        : await createDocumentTemplate({
          name, documentType, language, outputFormat,
          outputConfiguration: outputFormat === "ZPL" ? { dpi: zplDpi } : {}, template
        });
      await refresh(next);
      setMessage(t("documents.messages.saved", { number: next.number }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.errors.save"));
    } finally { setBusy(false); }
  };

  const act = async (
    action: () => Promise<DocumentTemplate>, success: string
  ) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const next = await action();
      await refresh(next);
      setMessage(success);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.errors.action"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected || !window.confirm(t("documents.confirm.deleteDraft", { number: selected.number }))) return;
    setBusy(true); setError(null);
    try {
      await deleteDocumentTemplate(selected.id, selected.version);
      setSelected(null); newTemplate();
      await refresh();
      setMessage(t("documents.messages.draftDeleted"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.errors.delete"));
    } finally { setBusy(false); }
  };

  const exportTemplates = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const includeAutomation = window.confirm(t("documents.transfer.includeAutomationConfirm"));
      const transfer = await exportDocumentTemplates(includeAutomation);
      const blob = new Blob([JSON.stringify(transfer, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `obviowms-document-templates-${transfer.organization}-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click(); URL.revokeObjectURL(url);
      setMessage(t("documents.transfer.exportSuccess"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.transfer.exportError"));
    } finally { setBusy(false); }
  };

  const importTemplates = async (file?: File) => {
    if (!file) return;
    setError(null); setMessage(null);
    try {
      const transfer = JSON.parse(await file.text()) as DocumentTemplateTransferFile;
      if (transfer.schemaVersion < 1 || transfer.schemaVersion > 3
          || !transfer.organization || !Array.isArray(transfer.families)) {
        throw new Error(t("documents.transfer.invalidFile"));
      }
      const count = transfer.families.reduce((sum, family) =>
        sum + (Array.isArray(family.variants) ? family.variants.length : 0), 0);
      if (!window.confirm(t("documents.transfer.importConfirm", {
        count, rules: transfer.automationRules?.length ?? 0,
        organization: transfer.organization
      }))) return;
      setBusy(true);
      const result = await importDocumentTemplates(transfer);
      setSelected(null); newTemplate(); await refresh();
      setMessage(t("documents.transfer.importSuccess", {
        added: result.added, updated: result.updated, unchanged: result.unchanged,
        rulesAdded: result.rulesAdded, rulesUpdated: result.rulesUpdated,
        rulesUnchanged: result.rulesUnchanged
      }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("documents.transfer.importError"));
    } finally { setBusy(false); }
  };

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <Paper sx={{ px: { xs: 1.5, md: 2 }, py: .75, borderTop: 4, borderTopColor: "secondary.main" }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ lg: "center" }}>
          <Button startIcon={<ArrowBackOutlined />} onClick={onBack}>{t("documents.actions.home")}</Button>
          <DescriptionOutlined color="secondary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={900}>{t("documents.module")}</Typography>
          </Box>
          <Button startIcon={<AddOutlined />} variant="outlined" onClick={newTemplate}
            disabled={!canManage || busy}>{t("documents.actions.new")}</Button>
          <Button startIcon={<SaveOutlined />} variant="contained" onClick={() => void save()}
            disabled={!canManage || busy || !designerReady || selected?.status !== "DRAFT" && Boolean(selected)}>
            {t("documents.actions.save")}
          </Button>
          {canManage && <Button startIcon={<DownloadOutlined />} onClick={() => void exportTemplates()}
            disabled={busy}>{t("documents.actions.export")}</Button>}
          {canManage && <Button component="label" startIcon={<UploadFileOutlined />} disabled={busy}>
            {t("documents.actions.import")}
            <input hidden type="file" accept="application/json,.json" onChange={(event) => {
              void importTemplates(event.target.files?.[0]); event.currentTarget.value = "";
            }} />
          </Button>}
          <Button startIcon={<OpenInNewOutlined />} href={url} target="_blank" rel="noreferrer">
            {t("documents.actions.fullScreen")}
          </Button>
          <Button startIcon={<TuneOutlined />} onClick={() => setAutomationOpen(true)}>
            {t("documents.actions.automation")}
          </Button>
          <Button startIcon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
            {t("documents.actions.history")}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}

      <Stack direction="row" spacing={1} sx={{ flex: 1, minHeight: 0 }}>
        {catalogOpen && <Paper sx={{ width: 260, flexShrink: 0, overflow: "auto" }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography fontWeight={900}>{t("documents.catalog.title")}</Typography>
              <Tooltip title={t("documents.catalog.collapse")}>
                <Button size="small" onClick={() => setCatalogOpen(false)}
                  sx={{ minWidth: 34, px: .5 }}><ChevronLeftOutlined /></Button>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("documents.catalog.count", { count: filteredTemplates.length })}
            </Typography>
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>{t("documents.catalog.languageFilter")}</InputLabel>
              <Select label={t("documents.catalog.languageFilter")} value={catalogLanguage}
                onChange={(event) => setCatalogLanguage(event.target.value as typeof catalogLanguage)}>
                <MenuItem value="fr">{t("documents.languages.fr")}</MenuItem>
                <MenuItem value="en">{t("documents.languages.en")}</MenuItem>
                <MenuItem value="all">{t("documents.catalog.allLanguages")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Divider />
          {templates.isLoading ? <Stack alignItems="center" sx={{ p: 3 }}><CircularProgress /></Stack>
            : templates.isError ? <Alert severity="error" sx={{ m: 1 }}>{t("documents.catalog.unavailable")}</Alert>
              : <List disablePadding>
                {filteredTemplates.map((template) => (
                  <ListItemButton key={template.id} selected={selected?.id === template.id}
                    onClick={() => selectTemplate(template)} alignItems="flex-start">
                    <ListItemText primary={template.name}
                      primaryTypographyProps={{ fontWeight: 800 }}
                      secondary={<Stack component="span" direction="row" spacing={.75} sx={{ mt: .5 }}>
                        <Typography component="span" variant="caption">{template.number}</Typography>
                        {template.availableLanguages.map((availableLanguage) =>
                          <Chip component="span" key={availableLanguage} size="small"
                            label={availableLanguage.toUpperCase()}
                            color={availableLanguage === template.language ? "primary" : "default"}
                            variant="outlined" sx={{ height: 20 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              const variant = (templates.data ?? []).find((candidate) =>
                                candidate.familyId === template.familyId
                                  && candidate.language === availableLanguage
                              );
                              if (variant) selectTemplate(variant);
                            }} />)}
                        <Chip component="span" size="small" label={t(`documents.status.${template.status}`)}
                          color={statusColor(template.status)} sx={{ height: 20 }} />
                        <Chip component="span" size="small" label={template.outputFormat}
                          variant="outlined" sx={{ height: 20 }} />
                      </Stack>} />
                  </ListItemButton>
                ))}
              </List>}
          {dictionary.data && <>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Typography fontWeight={900}>{t("documents.dictionary.title")}</Typography>
              <Typography variant="caption" color="text.secondary">
                {dictionaryContextLabel(dictionary.data.context)} · v{dictionary.data.version}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: .75, mb: 1 }}>
                {t("documents.dictionary.instruction")}
              </Typography>
              <Stack spacing={.5}>
                {dictionary.data.fields.map((field) => (
                  <Button key={field.key} size="small" variant="outlined"
                    color={field.type === "COLLECTION" ? "secondary" : "primary"}
                    disabled={!canManage || !designerReady}
                    onClick={() => bindField(field)}
                    sx={{ justifyContent: "flex-start", textTransform: "none" }}>
                    {dictionaryFieldLabel(field)}
                  </Button>
                ))}
              </Stack>
            </Box>
          </>}
        </Paper>}

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <Paper sx={{ px: 1, py: .75 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {!catalogOpen && <Tooltip title={t("documents.catalog.show")}>
                <Button size="small" variant="outlined" onClick={() => setCatalogOpen(true)}
                  sx={{ minWidth: 38, px: .5 }}><ChevronRightOutlined /></Button>
              </Tooltip>}
              <TextField label={t("documents.fields.templateName")} size="small" value={name}
                onChange={(event) => setName(event.target.value)} sx={{ flex: "1 1 260px", minWidth: 220 }}
                disabled={!canManage || selected?.status !== "DRAFT" && Boolean(selected)} />
              <FormControl size="small" sx={{ flex: "0 1 260px", minWidth: 210 }}>
                <InputLabel>{t("documents.fields.documentType")}</InputLabel>
                <Select label={t("documents.fields.documentType")} value={documentType}
                  onChange={(event) => setDocumentType(event.target.value)}
                  disabled={!canManage || Boolean(selected)}>
                  {documentTypes.map((value) => <MenuItem key={value} value={value}>{t(`documents.types.${value}`)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 150 }}>
                <InputLabel>{t("documents.fields.language")}</InputLabel>
                <Select label={t("documents.fields.language")} value={language}
                  onChange={(event) => selectLanguage(event.target.value)}
                  disabled={!canManage && !selected}>
                  {(["fr", "en"] as const)
                    .filter((value) => !selected || selected.availableLanguages.includes(value))
                    .map((value) => <MenuItem key={value} value={value}>
                      {t(`documents.languages.${value}`)}
                    </MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 130 }}>
                <InputLabel>{t("documents.fields.outputFormat")}</InputLabel>
                <Select label={t("documents.fields.outputFormat")} value={outputFormat}
                  onChange={(event) => {
                    const format = event.target.value as DocumentOutputFormat;
                    setOutputFormat(format);
                    if (!selected) send({
                      type: "new-template", outputFormat: format, dpi: zplDpi,
                      width: zplWidth, height: zplHeight
                    });
                  }} disabled={!canManage || Boolean(selected)}>
                  <MenuItem value="PDF">PDF</MenuItem>
                  <MenuItem value="ZPL">ZPL</MenuItem>
                </Select>
              </FormControl>
              {outputFormat === "ZPL" && <FormControl size="small" sx={{ width: 130 }}>
                <InputLabel>{t("documents.fields.zplDpi")}</InputLabel>
                <Select label={t("documents.fields.zplDpi")} value={zplDpi}
                  onChange={(event) => setZplDpi(Number(event.target.value) as 203 | 300 | 600)}
                  disabled={!canManage || selected?.status !== "DRAFT" && Boolean(selected)}>
                  {[203, 300, 600].map((dpi) => <MenuItem key={dpi} value={dpi}>{dpi} dpi</MenuItem>)}
                </Select>
              </FormControl>}
              {outputFormat === "ZPL" && <>
                <TextField label={interfaceLanguage === "fr" ? "Largeur (mm)" : "Width (mm)"}
                  size="small" type="number"
                  value={zplWidth} slotProps={{ htmlInput: { min: 10, max: 300, step: 1 } }}
                  sx={{ width: 115 }} disabled={!canManage || selected?.status !== "DRAFT" && Boolean(selected)}
                  onChange={(event) => {
                    const width = Number(event.target.value);
                    setZplWidth(width);
                    if (width > 0) send({ type: "resize-zpl-template", width, height: zplHeight });
                  }} />
                <TextField label={interfaceLanguage === "fr" ? "Hauteur (mm)" : "Height (mm)"}
                  size="small" type="number"
                  value={zplHeight} slotProps={{ htmlInput: { min: 10, max: 1000, step: 1 } }}
                  sx={{ width: 115 }} disabled={!canManage || selected?.status !== "DRAFT" && Boolean(selected)}
                  onChange={(event) => {
                    const height = Number(event.target.value);
                    setZplHeight(height);
                    if (height > 0) send({ type: "resize-zpl-template", width: zplWidth, height });
                  }} />
              </>}
              {selected && <Chip label={`${selected.number} · v${selected.version}`} variant="outlined" />}
              {selected?.status === "DRAFT" && canManage && <>
                <Button startIcon={<PublishOutlined />} disabled={busy}
                  onClick={() => void act(
                    () => publishDocumentTemplate(selected.id, selected.version),
                    t("documents.messages.published")
                  )}>{t("documents.actions.publish")}</Button>
                <Button color="error" startIcon={<DeleteOutlineOutlined />} disabled={busy}
                  onClick={() => void remove()}>{t("documents.actions.delete")}</Button>
              </>}
              {selected && canManage && <Button startIcon={<ContentCopyOutlined />} disabled={busy}
                onClick={() => void act(
                  () => duplicateDocumentTemplate(selected.id), t("documents.messages.copied")
                )}>{t("documents.actions.duplicate")}</Button>}
              {selected && canManage && selected.availableLanguages.length < 2 &&
                <Button startIcon={<ContentCopyOutlined />} disabled={busy}
                onClick={openTranslationDialog}>{t("documents.actions.duplicateTranslation")}</Button>}
              {selected && selected.status !== "ARCHIVED" && canManage &&
                <Button startIcon={<ArchiveOutlined />} disabled={busy}
                  onClick={() => void act(
                    () => archiveDocumentTemplate(selected.id, selected.version),
                    t("documents.messages.archived")
                  )}>{t("documents.actions.archive")}</Button>}
            </Stack>
          </Paper>

          {!designerReady && <Alert severity="info">{t("documents.designer.connecting")}</Alert>}
          <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}>
            {busy && <CircularProgress size={28} sx={{ position: "absolute", zIndex: 2, top: 12, right: 12 }} />}
            <Box ref={iframeRef} component="iframe" title={t("documents.designer.title")}
              src={url} sx={{ width: "100%", height: "100%", minHeight: 0, border: 0 }} />
          </Paper>
        </Stack>
      </Stack>

      <Dialog open={automationOpen} onClose={() => setAutomationOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{t("documents.automation.title")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>{t("documents.automation.rules")}</Typography>
          {canManage && <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <TextField size="small" label={t("documents.fields.ruleName")} value={ruleName}
                onChange={(event) => setRuleName(event.target.value)} sx={{ flex: 1 }} />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>{t("documents.fields.flowStep")}</InputLabel>
                <Select label={t("documents.fields.flowStep")} value={ruleAnchor}
                  onChange={(event) => setRuleAnchor(event.target.value)}>
                  {(anchors.data ?? []).map((anchor) =>
                    <MenuItem key={anchor.code} value={anchor.code}>
                      {anchorLabel(anchor.code, anchor.name)}
                    </MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 230 }}>
                <InputLabel>{t("documents.fields.publishedTemplate")}</InputLabel>
                <Select label={t("documents.fields.publishedTemplate")} value={ruleTemplate}
                  onChange={(event) => setRuleTemplate(event.target.value)}>
                  {(templates.data ?? []).filter((template) => template.status === "PUBLISHED").map((template) =>
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} · {template.language.toUpperCase()}
                    </MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>{t("documents.fields.action")}</InputLabel>
                <Select label={t("documents.fields.action")} value={ruleAction}
                  onChange={(event) => setRuleAction(event.target.value as typeof ruleAction)}>
                  <MenuItem value="GENERATE">{t("documents.actions.generate")}</MenuItem>
                  <MenuItem value="GENERATE_AND_PRINT">{t("documents.actions.generatePrint")}</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" label={t("documents.fields.copies")} type="number" value={ruleCopies}
                onChange={(event) => setRuleCopies(Number(event.target.value))}
                inputProps={{ min: 1, max: 99 }} sx={{ width: 90 }} />
            </Stack>
            {ruleAction === "GENERATE_AND_PRINT" && <TextField size="small" fullWidth
              label={t("documents.fields.printer")} value={rulePrinter}
              onChange={(event) => setRulePrinter(event.target.value)} sx={{ mt: 1 }} />}
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button variant="contained" disabled={ruleBusy || !ruleTemplate || ruleName.trim().length < 1
                || ruleAction === "GENERATE_AND_PRINT" && !rulePrinter.trim()}
                onClick={() => void createRule()}>{t("documents.actions.addRule")}</Button>
            </Stack>
          </Paper>}
          {rules.isLoading ? <CircularProgress size={24} /> : <Stack spacing={1} sx={{ mb: 3 }}>
            {(rules.data ?? []).map((rule) => (
              <Paper key={rule.id} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={900}>{ruleNameLabel(rule.name)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(() => {
                        const anchor = (anchors.data ?? []).find((item) => item.code === rule.anchorCode);
                        return anchorLabel(rule.anchorCode, anchor?.name ?? rule.anchorCode);
                      })()}
                      {" → "}{rule.templateName} · {rule.templateLanguage.toUpperCase()} · {rule.action === "GENERATE" ? t("documents.actions.generate") : t("documents.actions.generatePrint")}
                      {rule.copies > 1 ? ` · ${t("documents.automation.copies", { count: rule.copies })}` : ""}
                    </Typography>
                  </Box>
                  <Chip size="small" variant="outlined" label={rule.number} />
                  <Typography variant="body2">{t(`documents.status.${rule.active ? "active" : "inactive"}`)}</Typography>
                  <Switch checked={rule.active} disabled={!canManage || ruleBusy}
                    onChange={() => void toggleRule(rule)} />
                  {canManage && <Button color="error" startIcon={<DeleteOutlineOutlined />}
                    disabled={ruleBusy} onClick={() => void removeRule(rule)}>{t("documents.actions.delete")}</Button>}
                </Stack>
              </Paper>
            ))}
          </Stack>}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAutomationOpen(false)}>{t("documents.actions.close")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={translationDialogOpen} onClose={() => setTranslationDialogOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>{t("documents.translation.title")}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>{t("documents.translation.explanation")}</Alert>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t("documents.translation.targetLanguage")}</InputLabel>
              <Select label={t("documents.translation.targetLanguage")} value={translationLanguage}
                onChange={(event) => setTranslationLanguage(event.target.value as "fr" | "en")}>
                {selected?.language !== "fr" && <MenuItem value="fr">{t("documents.languages.fr")}</MenuItem>}
                {selected?.language !== "en" && <MenuItem value="en">{t("documents.languages.en")}</MenuItem>}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranslationDialogOpen(false)}>{t("documents.actions.cancel")}</Button>
          <Button variant="contained" disabled={busy}
            onClick={() => void duplicateTranslation()}>{t("documents.translation.create")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{t("documents.history.title")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("documents.history.description")}
          </Typography>
          {automationJobs.isLoading ? <Stack alignItems="center" sx={{ p: 3 }}><CircularProgress /></Stack>
            : automationJobs.isError ? <Alert severity="error">{t("documents.history.unavailable")}</Alert>
              : (automationJobs.data ?? []).length === 0
                ? <Alert severity="info">{t("documents.history.empty")}</Alert>
                : <Stack spacing={1}>
                  {(automationJobs.data ?? []).map((job) => (
                    <Paper key={job.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={900}>
                            {job.entityNumber} · {ruleNameLabel(job.ruleName)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {anchorLabel(job.anchorCode, job.anchorCode)} · {job.templateNumber} · {new Date(job.createdAt).toLocaleString(locale)}
                          </Typography>
                          {job.lastError && <Typography variant="caption" color="error.main">
                            {job.lastError}
                          </Typography>}
                        </Box>
                        <Chip label={job.status === "FAILED"
                          ? t("documents.status.FAILED", { attempts: job.attempts })
                          : t(`documents.status.${job.status}`)}
                          color={job.status === "COMPLETED" ? "success" : job.status === "FAILED" ? "error" : "warning"} />
                        {job.action === "GENERATE_AND_PRINT" &&
                          <Chip variant="outlined" label={`${t("documents.status.print", {
                            status: t(`documents.status.printStatus.${job.printStatus}`,
                              { defaultValue: job.printStatus.toLowerCase() })
                          })}${job.printerCode ? ` · ${job.printerCode}` : ""}`} />}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void automationJobs.refetch()}>{t("documents.actions.refresh")}</Button>
          <Button variant="contained" onClick={() => setHistoryOpen(false)}>{t("documents.actions.close")}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
